import { create } from 'zustand'

import type { ModelsActionType } from '@/api/generated-api'
import type {
  AuthEntityType,
  AuthModalView
} from '@/app/[locale]/components/Auth/AuthModal/types'
import type { EntryPoint } from '@/lib/productAnalytics'

export type ModalType =
  | 'auth'
  | 'confirmation'
  | 'forgotPassword'
  | 'username'
  | 'recoverPassword'
  | 'passwordResetSuccess'
  | null

export interface ConfirmationData {
  email?: string
  actionType?: ModelsActionType
  verificationId?: string
  /** Sign-up only: the role picked, for «Back» to the form. */
  entityType?: AuthEntityType
}

/** One-shot state handed back to the sign-up form when it re-opens: after
 *  «Back» on the code screen, or after a Google sign-up that did not finish.
 *  US-ATH-01/02.3: the role, the email and the consent tick survive the trip.
 *  `noticeCode` is the auth error to show on the form (AU-206, AU-229). */
export interface SignupPrefill {
  email?: string
  consent?: boolean
  noticeCode?: string
}

export interface ConfirmationHandlers {
  onConfirm: (code: string) => Promise<void> | void
  onResend: () => Promise<string | undefined | void> | void
}

interface ModalState {
  activeModal: ModalType
  confirmationData: ConfirmationData | null
  confirmationHandlers: ConfirmationHandlers | null
  confirmationError: string
  /** OCE-569 (BUG-F): visually emphasises the Resend CTA when the backend
   *  reports AU-213 ErrOTPExpired so the user understands they need a new
   *  code, not another attempt with the old one. */
  confirmationHighlightResend: boolean
  authModalInitialView: AuthModalView
  authModalSuccessType: 'signup' | 'login'
  /** Product analytics: why the auth modal was opened; null → 'auth_modal'. */
  authModalEntryPoint: EntryPoint | null
  usernameSubmitHandler: ((username: string) => void) | null
  recoverPasswordSubmitHandler: ((password: string) => void) | null
  googleSignupToken: string | null
  referralCode: string | null
  referralEntityType: AuthEntityType | null
  /** Pre-selects USER/CLUB on the signup entity-choose step without locking it
   *  the way referralEntityType does. Used by entry-points that hint a desired
   *  role (e.g. "Register your club" CTA) but still let the user change mind. */
  signupDefaultEntityType: AuthEntityType | null
  /** OCE-661: one-shot prefill consumed by LoginForm when AuthModal re-opens
   *  after a failed-credentials SignIn (AU-205 / UP-100) discovered at the
   *  OTP step. The form drains both fields on mount via clearLoginPrefill().
   *  Password is intentionally NOT carried — the store can be inspected by
   *  browser tooling, so credentials must not leave RHF state. */
  loginPrefillEmail: string | null
  loginPrefillError: string | null
  signupPrefill: SignupPrefill | null

  openModal: (type: ModalType) => void
  closeAll: () => void
  openConfirmationModal: (
    data: ConfirmationData,
    handlers: ConfirmationHandlers
  ) => void
  setConfirmationError: (error: string) => void
  setConfirmationHighlightResend: (highlight: boolean) => void
  setAuthModalInitialView: (view: AuthModalView) => void
  setAuthModalSuccessType: (type: 'signup' | 'login') => void
  setAuthModalEntryPoint: (entryPoint: EntryPoint | null) => void
  setUsernameSubmitHandler: (
    handler: ((username: string) => void) | null
  ) => void
  setRecoverPasswordSubmitHandler: (
    handler: ((password: string) => void) | null
  ) => void
  setGoogleSignupToken: (token: string | null) => void
  setReferralCode: (code: string | null) => void
  setReferralEntityType: (type: AuthEntityType | null) => void
  setSignupDefaultEntityType: (type: AuthEntityType | null) => void
  setLoginPrefill: (email: string, error: string) => void
  clearLoginPrefill: () => void
  setSignupPrefill: (prefill: SignupPrefill | null) => void
}

export const useModalStore = create<ModalState>(set => ({
  activeModal: null,
  confirmationData: null,
  confirmationHandlers: null,
  confirmationError: '',
  confirmationHighlightResend: false,
  authModalInitialView: 'login',
  authModalSuccessType: 'signup',
  authModalEntryPoint: null,
  usernameSubmitHandler: null,
  recoverPasswordSubmitHandler: null,
  googleSignupToken: null,
  referralCode: null,
  referralEntityType: null,
  signupDefaultEntityType: null,
  loginPrefillEmail: null,
  loginPrefillError: null,
  signupPrefill: null,

  openModal: type => {
    set({ activeModal: type })
  },

  closeAll: () => {
    set({
      activeModal: null,
      confirmationData: null,
      confirmationHandlers: null,
      confirmationError: '',
      confirmationHighlightResend: false,
      usernameSubmitHandler: null,
      recoverPasswordSubmitHandler: null,
      googleSignupToken: null,
      loginPrefillEmail: null,
      loginPrefillError: null,
      authModalEntryPoint: null
    })
  },

  openConfirmationModal: (data, handlers) => {
    set({
      activeModal: 'confirmation',
      confirmationData: data,
      confirmationHandlers: handlers,
      confirmationError: '',
      confirmationHighlightResend: false
    })
  },

  setConfirmationError: error => {
    set({ confirmationError: error })
  },

  setConfirmationHighlightResend: highlight => {
    set({ confirmationHighlightResend: highlight })
  },

  setAuthModalInitialView: view => {
    set({ authModalInitialView: view })
  },

  setAuthModalSuccessType: type => {
    set({ authModalSuccessType: type })
  },

  setAuthModalEntryPoint: entryPoint => {
    set({ authModalEntryPoint: entryPoint })
  },

  setUsernameSubmitHandler: handler => {
    set({ usernameSubmitHandler: handler })
  },

  setRecoverPasswordSubmitHandler: handler => {
    set({ recoverPasswordSubmitHandler: handler })
  },

  setGoogleSignupToken: token => {
    set({ googleSignupToken: token })
  },

  setReferralCode: code => {
    set({ referralCode: code })
  },

  setReferralEntityType: type => {
    set({ referralEntityType: type })
  },

  setSignupDefaultEntityType: type => {
    set({ signupDefaultEntityType: type })
  },

  setLoginPrefill: (email, error) => {
    set({ loginPrefillEmail: email, loginPrefillError: error })
  },

  clearLoginPrefill: () => {
    set({ loginPrefillEmail: null, loginPrefillError: null })
  },

  setSignupPrefill: prefill => {
    set({ signupPrefill: prefill })
  }
}))
